// The schemas are a set of types, each associated with a set of properties.
// Schema are used to improve the way search engines read and represent our page.
// We use Schema in the JSON+LD format.
// You can read more at https://schema.org

interface questionJSONLDSchema {
  '@type': SchemaType;
  name: string;
  acceptedAnswer: {
    '@type': SchemaType;
    text: string;
  };
}

interface ListItemJSONLDSchema {
  '@type': SchemaType;
  position: number;
  url: string;
}

export enum SchemaType {
  ORGANIZATION = 'Organization',
  FAQ_PAGE = 'FAQPage',
  PRODUCT = 'Product',
  OFFER = 'Offer',
  LIST_ITEM = 'ListItem',
  ITEM_LIST = 'ItemList',
  QUESTION = 'Question',
  ANSWER = 'Answer',
  PERSON = 'Person',
  POSTAL_ADDRESS = 'PostalAddress',
  CONTACT_POINT = 'ContactPoint',
}

export enum Availability {
  OUT_OF_STOCK = 'https://schema.org/OutOfStock',
  IN_STOCK = 'https://schema.org/InStock',
}

/**
 * Structured data required by crawlers to match
 * data in Google Merchant feed. Please note that
 * SKU in this context is meant to match productId
 * rather than our internal use of "sku"
 */
export interface ProductJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  description: string;
  url: string;
  image: string | undefined;
  offers: {
    '@type': SchemaType;
    sku: string;
    availability: Availability;
    price: string;
    priceCurrency: string;
  };
}

/**
 * Structured data in format recommended by Google for
 * summary listing pages (used in our case for the PIP)
 * https://developers.google.com/search/docs/advanced/structured-data/carousel#summary-page
 */
export interface ItemListJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  itemListElement: ListItemJSONLDSchema[];
}

export interface FaqJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  mainEntity: questionJSONLDSchema[];
}

export interface OrganizationJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  legalName: string;
  url: string;
  logo: string;
  foundingDate: string;
  founders: {
    '@type': SchemaType;
    name: string;
  };
  address: {
    '@type': SchemaType;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint: {
    '@type': SchemaType;
    contactType: string;
    telephone: string;
    email: string;
  };
  sameAs: string[];
}

export type JSONLDSchema =
  | ProductJSONLDSchema
  | ItemListJSONLDSchema
  | FaqJSONLDSchema
  | OrganizationJSONLDSchema;
